import type { TeamTheme } from '@mondy/shared';

/**
 * Page-level ambient backdrop — renders behind the entire shell. When the
 * user has a favorite team this paints the whole viewport with radial washes
 * pulled from the team palette (primary top-left, accent bottom-right). The
 * flag itself lives in <App>'s `<main>` element so it anchors to the main
 * column's top-right corner instead of being covered by the standings rail.
 *
 * When no theme is set it falls back to the Continental '26 dual-radial
 * (hot top-right, sky bottom-left) + a diagonal-hairline decoration so the
 * page never feels flat.
 *
 * Must be mounted **once** inside `<App>`. position: fixed, inset: 0,
 * zIndex: -1 — sits behind everything else but transitions smoothly when
 * the user changes team (all color stops are driven by CSS vars).
 */
export function GlobalBackdrop({ theme: _theme }: { theme: TeamTheme | null }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {/* Layer 1 — base color.
          No team: --bg (neutral Continental). Team: --team-deep (which
          useApplyTeamThemeVars already swaps to `ink` for light-mode teams,
          so we get a dark navy for Argentina, not a blinding pale blue). */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--team-deep)',
          transition: 'background 0.3s ease',
        }}
      />

      {/* Layer 2 — broad primary wash from the top-left. The reference image
          is not a flat black page; the favorite team's main color tints the
          entire upper/left field. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(
            110% 90% at 12% -12%,
            var(--team-primary) 0%,
            color-mix(in oklab, var(--team-primary) 32%, transparent) 34%,
            transparent 66%
          )`,
          opacity: `calc(0.62 * var(--team-bg-strength))`,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Layer 3 — secondary flag-color wash. Kept wider and softer so teams
          like Mexico still read green-first with a restrained red undertone. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(
            85% 70% at 102% 112%,
            var(--team-accent) 0%,
            color-mix(in oklab, var(--team-accent) 25%, transparent) 35%,
            transparent 60%
          )`,
          opacity: `calc(0.26 * var(--team-bg-strength))`,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Layer 4 — angled shadow bands and a large bottom-right shade, close
          to the dark diagonal panels in the reference composition. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            linear-gradient(
              82deg,
              transparent 0 58%,
              rgba(255, 255, 255, 0.035) 58% 70%,
              transparent 70% 82%,
              rgba(255, 255, 255, 0.045) 82% 94%,
              transparent 94%
            ),
            radial-gradient(42% 52% at 78% 100%, rgba(0, 0, 0, 0.45), transparent 68%)
          `,
          opacity: `calc(0.82 * var(--team-bg-strength))`,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Layer 5 used to paint the flag here. It now lives inside <main>
          (see <MainColumnFlag />) so the standings rail can't cover it on
          wide viewports. */}

      {/* Layer 6 — neutral fallback washes for the no-team state. The
          existing Continental accent corner glows (hot top-right, sky
          bottom-left) so the app still feels alive without a favorite. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(1200px 600px at 90% -10%, var(--hot-soft), transparent 60%), radial-gradient(900px 500px at -10% 110%, var(--sky-soft), transparent 60%)',
          opacity: `calc(1 - var(--team-bg-strength))`,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Layer 7 — diagonal hairline decoration — inherited from the old
          .app-bg::before. Keeps the "texture of a real surface" feel across
          themed and neutral states. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'repeating-linear-gradient(115deg, transparent 0 38px, rgba(255, 255, 255, 0.015) 38px 39px)',
        }}
      />

      {/* Layer 8 — edge vignette. It pushes cards forward and prevents the
          stronger team color from flattening the page. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(80% 70% at 28% 12%, transparent 0%, transparent 46%, rgba(0, 0, 0, 0.28) 100%), linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.26) 100%)',
        }}
      />
    </div>
  );
}
